import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  authService,
  type MeUser,
  type LoginResponse,
} from "@/services/auth";
import { setAccessToken, registerLogoutHandler } from "@/services/api";
import { clearOnlinePromptPending } from "@/utils/presence-prompt";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<MeUser | null>(null);
  const permissions = ref<string[]>([]);
  const accessExpiresAt = ref<string | null>(null);
  const bootstrapped = ref(false);
  // justLoggedIn: marca que esta sessão veio de um LOGIN explícito (não de um
  // refresh). É EM MEMÓRIA de propósito — some no reload da página (novo
  // contexto JS), então o hydrate() do refresh nunca o liga. Consumido uma vez
  // pelo AppLayout para disparar o alerta de "ficar disponível" só no login.
  const justLoggedIn = ref(false);

  const isAuthenticated = computed(() => user.value !== null);

  function applyLogin(payload: LoginResponse) {
    user.value = payload.user;
    permissions.value = payload.permissions ?? [];
    accessExpiresAt.value = payload.access_expires_at;
    setAccessToken(payload.access_token);
  }

  function clear() {
    user.value = null;
    permissions.value = [];
    accessExpiresAt.value = null;
    justLoggedIn.value = false;
    // Fim de sessão: descarta pendência do alerta de status (não vaza p/ o
    // próximo login na mesma aba).
    clearOnlinePromptPending();
    setAccessToken(null);
  }

  async function login(email: string, password: string, opts: { orgSlug?: string; totp?: string } = {}) {
    const resp = await authService.login(email, password, opts);
    applyLogin(resp);
    justLoggedIn.value = true;
    return resp;
  }

  // consumeJustLoggedIn devolve se esta montagem é a do pós-login e zera o flag
  // (uma vez por login). Chamado pelo AppLayout para decidir o alerta de status.
  function consumeJustLoggedIn(): boolean {
    const v = justLoggedIn.value;
    justLoggedIn.value = false;
    return v;
  }

  async function logout() {
    // Best-effort: a failed network call doesn't change the user's
    // intent to be logged out. We always clear local state and never
    // surface the network error to the caller.
    try {
      await authService.logout();
    } catch {
      /* swallow */
    } finally {
      clear();
    }
  }

  // Hydrate runs once at app start. If the refresh cookie is valid we end
  // up with a fresh access token + user; otherwise we land logged out.
  async function hydrate() {
    if (bootstrapped.value) return;
    try {
      // /v1/me requires Bearer. We rely on the refresh interceptor: the
      // first /me call returns 401, the interceptor refreshes, then
      // /me retries successfully. Failure means no refresh cookie.
      const me = await authService.me();
      user.value = me.user;
      permissions.value = me.permissions ?? [];
    } catch {
      clear();
    } finally {
      bootstrapped.value = true;
    }
  }

  function hasPermission(code: string): boolean {
    return (
      permissions.value.includes(code) ||
      permissions.value.includes("org:manage_all")
    );
  }

  registerLogoutHandler(clear);

  return {
    user,
    permissions,
    accessExpiresAt,
    bootstrapped,
    justLoggedIn,
    isAuthenticated,
    applyLogin,
    clear,
    login,
    logout,
    hydrate,
    hasPermission,
    consumeJustLoggedIn,
  };
});
